import {
	Avatar,
	Button,
	Flex,
	Heading,
	Text,
	useColorModeValue,
} from '@chakra-ui/react';
import {
	AttachMoney,
	Link as LinkOutlined,
	ReceiptLong,
	SyncAltOutlined,
} from '@mui/icons-material';
import balanceImg from 'assets/img/dashboards/balanceImg.png';
import Card from 'components/card/Card.js';
import { VSeparator } from 'components/separator/Separator';
import CopyableChip from 'components/table/copyableChip';
import IconTabButtons from 'components/tabs/IconTabButtons';
import { NAVIGATE } from 'constant/navigation';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { NavLink, useNavigate } from 'react-router-dom';
import {
	fetchConversionRates,
	formatBalance,
	getSatoshis,
	handleCurrencyChange,
	satoshiToMxn,
} from 'utils/rates';

export default function Banner(props) {
	const { profile } = props;

	const navigate = useNavigate();
	const textColor = useColorModeValue('gray.900', 'gray.100');
	const headerColor = useColorModeValue('gray.100', 'gray.900');
	const balanceBg = useColorModeValue('brand.900', 'gray.100');
	const cardBalanceBg = useColorModeValue('background.100', 'gray.500');
	const {
		userFirstName,
		userBalanceAvailable,
		userBalancePending,
		userBalanceTotal,
		userReferralCode,
		userReferrals,
	} = useSelector((state) => state.user);

	const [mxnValue, setMxnValue] = useState(0);
	const [usdValue, setUsdValue] = useState(0);
	const [currentCurrency, setCurrentCurrency] = useState('SATS');

	useEffect(() => {
		fetchConversionRates(setMxnValue, setUsdValue);
	}, []);

	const tabConfig = [
		{ label: 'conversiones', icon: AttachMoney },
		{ label: 'enlaces', icon: LinkOutlined },
		{ label: 'retiros', icon: SyncAltOutlined },
		{ label: 'historial', icon: ReceiptLong },
	];

	const getPathObj = (tab) => ({
		path: '/client/profile/activity',
		state: { activeTab: tab },
	});

	const tabs = tabConfig.map(({ label, icon }) => ({
		label,
		icon,
		onClick: () => {
			const { path, state } = getPathObj(label);
			navigate(path, { state });
		},
	}));

	return (
		<Flex
			mb={{ base: '20px', '2xl': '20px' }}
			m={{ base: '0' }}
			justifyContent='center'
			align='center'
			direction='column'
			w={{ base: 'auto', md: '100%' }}
			mx='1rem'
		>
			<Avatar
				src={profile}
				h={{ base: '70px', md: '170px' }}
				w={{ base: '70px', md: '170px' }}
				mb='20px'
			/>
			<Heading
				color={textColor}
				fontSize={{ base: '32px', md: '54px' }}
				fontWeight='700'
				lineHeight='100%'
				textAlign='center'
			>
				Hola, {userFirstName ? `${userFirstName}` : 'usuario'}
			</Heading>
			<Flex order={{ base: 4, md: 1 }}>
				<NavLink to={NAVIGATE.BRANDS}>
					<Button
						px='1rem'
						variant='brand'
						color={headerColor}
						my={{ base: '0.5rem', md: '2rem' }}
					>
						Explorar marcas
					</Button>
				</NavLink>
			</Flex>
			<IconTabButtons tabs={tabs} wrapperProps={{ mt: 4 }} />
			<Card
				order={{ base: 1, md: 2 }}
				bgColor={cardBalanceBg}
				maxW='100%'
				w='900px'
				py='40px'
				mb='40px'
				mt={{ base: '40px', md: '0' }}
			>
				<Flex
					justify='space-between'
					p='20px'
					mb='20px'
					borderRadius='16px'
					bgColor={balanceBg}
					bgImage={balanceImg}
					bgPosition='right'
					bgSize='cover'
					transition='all 0.3s'
					cursor='pointer'
					onClick={() => handleCurrencyChange(setCurrentCurrency)}
				>
					<Flex
						color={headerColor}
						align='center'
						justify='space-between'
						w='100%'
					>
						<Flex flexDirection='column'>
							<Text fontSize='sm' fontWeight='500' mb={2}>
								Balance total
							</Text>
							<Text
								fontSize={{ base: '24px', md: '34px' }}
								fontWeight='700'
								lineHeight='100%'
							>
								{formatBalance(
									userBalanceTotal,
									currentCurrency,
									usdValue,
									mxnValue
								)}
							</Text>
							<Text mt={1} fontSize='14px' fontWeight='300' lineHeight='100%'>
								{currentCurrency === 'MXN'
									? `${getSatoshis(userBalanceTotal)} sats`
									: `$${satoshiToMxn(userBalanceTotal, mxnValue)} MXN`}
							</Text>
						</Flex>
					</Flex>
				</Flex>
				<Flex
					w='100%'
					justify={{ base: 'center', md: 'space-around' }}
					direction={{ base: 'column', md: 'row' }}
				>
					<Flex
						direction='column'
						align='center'
						mb={{ base: '20px', md: '0px' }}
						transition='all 0.3s'
						_hover={{ transform: 'scale(1.05)' }}
						cursor='pointer'
						onClick={() => navigate(NAVIGATE.WALLET)}
					>
						<Flex align='center'>
							<Text
								color={textColor}
								fontSize={{ base: '24px', lg: '32px' }}
								fontWeight='700'
							>
								{formatBalance(
									userBalanceAvailable,
									currentCurrency,
									usdValue,
									mxnValue
								)}
							</Text>
						</Flex>
						<Text color={textColor} fontSize='sm' fontWeight='500'>
							Balance disponible
						</Text>
					</Flex>
					<VSeparator />
					<Flex
						direction='column'
						align='center'
						transition='all 0.3s'
						_hover={{ transform: 'scale(1.05)' }}
						cursor='pointer'
						onClick={() => navigate(NAVIGATE.WALLET)}
					>
						<Flex align='center'>
							<Text
								color={textColor}
								fontSize={{ base: '24px', lg: '32px' }}
								fontWeight='700'
							>
								{formatBalance(
									userBalancePending,
									currentCurrency,
									usdValue,
									mxnValue
								)}
							</Text>
						</Flex>
						<Text color={textColor} fontSize='sm' fontWeight='500'>
							Balance pendiente
						</Text>
					</Flex>
				</Flex>
			</Card>
			<Flex
				order={{ base: 3, md: 3 }}
				flexDirection='column'
				gap={'30px'}
				mb={'40px'}
				textAlign={'center'}
			>
				<Text color={textColor} fontSize='lg' fontWeight='500'>
					Refiere a tus amigos 🎉
				</Text>

				<CopyableChip
					value={`go.bumbei.com/inicio?r=${userReferralCode}`}
					copyable={true}
				/>
			</Flex>
			<Card
				order={{ base: 2, md: 4 }}
				bgColor={cardBalanceBg}
				maxW='100%'
				w='900px'
				py='40px'
				mb='40px'
			>
				<Flex
					direction='column'
					align='center'
					mb={{ base: '20px', md: '0px' }}
				>
					<Flex align='center'>
						<Text
							color={textColor}
							fontSize={{ base: '24px', lg: '32px' }}
							fontWeight='700'
						>
							{userReferrals}
						</Text>
					</Flex>
					<Text color={textColor} fontSize='sm' fontWeight='500'>
						Total referidos
					</Text>
				</Flex>
			</Card>
		</Flex>
	);
}
